import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  MESSAGE_PROTOCOL_VERSION,
  MESSAGE_TYPES,
  MIN_SUPPORTED_VERSION,
  ReceiveMessageType,
  SendMessageType,
  WidgetDataPayload,
} from 'src/@types/message';
import { isValidMessage, isVersionSupported, parseReceiveMessage } from 'src/@types/message.schema';
import { isBrowser, isNativeApp as checkIsNativeApp } from 'src/utils/environment';

declare global {
  interface DocumentEventMap {
    message: MessageEvent;
  }
}

type MessageHandler = (message: ReceiveMessageType) => void;

const useIsomorphicLayoutEffect = isBrowser() ? useLayoutEffect : useEffect;

// message 전송 함수 to React Native WebView
export const sendMessage = (message: SendMessageType) => {
  const messageWithVersion = {
    ...message,
    version: MESSAGE_PROTOCOL_VERSION,
  };
  window.ReactNativeWebView?.postMessage(JSON.stringify(messageWithVersion));
};

// 위젯 데이터 전송 함수
export const sendWidgetData = (payload: WidgetDataPayload) => {
  const message = {
    type: MESSAGE_TYPES.WIDGET_DATA,
    payload,
  };
  sendMessage(message);
};

/**
 * React Native WebView 통신을 위한 커스텀 훅
 * - React Native WebView에서 postMessage로 보낸 메시지를 받아서 처리할 수 있습니다.
 * - sendMessage 함수를 통해 React Native WebView로 메시지를 보낼 수 있습니다.
 * @param handler 메시지를 받아서 처리할 함수
 */
const useWebViewCommunication = (handler?: MessageHandler) => {
  const isNativeApp = checkIsNativeApp();

  // Create a ref that stores handler
  const savedHandler = useRef(handler);

  useIsomorphicLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // 브라우저 환경에서 허용할 origin 목록 (WebView에서는 검증 불필요)
    const ALLOWED_ORIGINS = [
      window.location.origin, // 현재 origin
    ];

    const handleMessage = (event: MessageEvent) => {
      // 1단계: origin 검증 (JSON 파싱 이전)
      if (!checkIsNativeApp() && event.origin) {
        if (!ALLOWED_ORIGINS.includes(event.origin)) {
          return;
        }
      }

      // 2단계: JSON 파싱 (파싱 실패는 조용히 무시)
      let parsed: unknown;
      try {
        if (!event.data || typeof event.data !== 'string') return;
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      // 3단계: Zod 스키마 검증
      if (!isValidMessage(parsed)) {
        return;
      }

      const message = parseReceiveMessage(parsed);
      if (!message) return;

      // 4단계: 버전 체크
      const messageVersion = (parsed as { version?: string }).version;
      if (messageVersion && !isVersionSupported(messageVersion, MIN_SUPPORTED_VERSION)) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            `[WebView] Received message with unsupported version: ${messageVersion}. ` +
              `Minimum supported: ${MIN_SUPPORTED_VERSION}. Message will still be processed.`,
          );
        }
      }

      // 5단계: 핸들러 실행 (핸들러 에러는 개발환경에서 로깅)
      try {
        savedHandler.current?.(message as ReceiveMessageType);
      } catch (handlerError) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[WebView] Message handler error:', handlerError);
        }
      }
    };

    document.addEventListener('message', handleMessage); // For Android
    window.addEventListener('message', handleMessage); // For iOS

    return () => {
      document.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleMessage);
    };
  }, []); // Empty deps - handler changes handled via ref

  return { sendMessage, isNativeApp };
};

export default useWebViewCommunication;
